import type { ReactNode } from 'react';
import { formatDate } from '@koob/dates';
import { computeEndDate } from './buildTripPayload';
import type { StepDef } from './steps';
import type { TripWizardConfig, WizardValues } from './types';

interface ReviewSummaryProps {
  config: TripWizardConfig;
  values: WizardValues;
  steps: StepDef[];
  /** Opens the single-screen editing layout scrolled to that step's section. */
  onEditStep: (stepId: string) => void;
}

export function ReviewSummary({
  config,
  values,
  steps,
  onEditStep,
}: ReviewSummaryProps) {
  const { t } = config;
  const dash = t('summary.empty');
  const stepIndexById = (id: string) => steps.findIndex((s) => s.id === id);

  const jump = (id: string) => {
    if (stepIndexById(id) >= 0) onEditStep(id);
  };

  const longDate = (d: Date) => formatDate(d, 'dddd D MMMM YYYY');

  const end = computeEndDate(values.departureDate, values.duration);
  const childrenCount = values.childrenBirthdates.length;

  const travellers = [
    t('summary.adultsCount', { count: values.adultPax }),
    childrenCount > 0
      ? t('summary.childrenCount', { count: childrenCount })
      : null,
  ]
    .filter(Boolean)
    .join(', ');

  const rooms = values.rooms
    .map(
      (r, i) =>
        `${t('rooms.room', { index: i + 1 })}: ${r.adults}A${r.children ? ` ${r.children}C` : ''}`,
    )
    .join(' · ');

  const rows: { id: string; label: string; value: ReactNode }[] = [
    {
      id: 'countries',
      label: t('summary.destinations'),
      value: values.countries.map((c) => c.label).join(', ') || dash,
    },
    ...(values.dmcLabel
      ? [{ id: 'dmc', label: t('summary.dmc'), value: values.dmcLabel }]
      : []),
    {
      id: 'location',
      label: t('summary.itinerary'),
      value: `${values.startLocation?.title ?? dash} → ${values.endLocation?.title ?? dash}`,
    },
    {
      id: 'dates',
      label: t('summary.departure'),
      value: values.departureDate ? longDate(values.departureDate) : dash,
    },
    {
      id: 'dates',
      label: t('summary.duration'),
      value: t('summary.daysCount', { count: values.duration }),
    },
    {
      id: 'travellers',
      label: t('summary.travellers'),
      value: travellers || dash,
    },
    { id: 'rooms', label: t('summary.rooms'), value: rooms || dash },
    {
      id: 'lookingFor',
      label: t('summary.themes'),
      value: values.themes.map((tag) => tag.label).join(', ') || dash,
    },
    {
      id: 'lookingFor',
      label: t('summary.hotelStyles'),
      value: values.hotelStyles.map((tag) => tag.label).join(', ') || dash,
    },
    { id: 'brief', label: t('summary.brief'), value: values.brief || dash },
    // Countries and DMC are shown even without their step (a DMC host, a trip
    // designed from a template): the trip still goes there, with that DMC.
  ].filter(
    (r) => stepIndexById(r.id) >= 0 || r.id === 'countries' || r.id === 'dmc',
  );

  return (
    <div className="mx-auto w-full max-w-xl">
      <span className="text-xs uppercase tracking-wide text-brand">
        {t('summary.eyebrow')}
      </span>
      <div className="mt-1 flex items-start justify-between">
        <h2 className="m-0 text-3xl font-semibold text-gray-900">
          {values.name || dash}
        </h2>
        <button
          type="button"
          className="border-none bg-transparent text-sm text-gray-400 hover:text-brand"
          onClick={() => jump('name')}
        >
          ✎ {t('summary.edit')}
        </button>
      </div>
      <p className="mb-6 mt-2 text-sm text-gray-400">{t('summary.hint')}</p>

      <div className="divide-y divide-gray-100 rounded-xl border border-gray-100">
        {rows.map((row, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3">
            <span className="w-32 shrink-0 text-xs uppercase tracking-wide text-gray-400">
              {row.label}
            </span>
            <span className="flex-1 text-sm text-gray-800">{row.value}</span>
            {/* No step to jump to: the answer is fixed, read-only. */}
            {stepIndexById(row.id) >= 0 && (
              <button
                type="button"
                className="border-none bg-transparent text-xs text-gray-400 hover:text-brand"
                onClick={() => jump(row.id)}
              >
                ✎ {t('summary.edit')}
              </button>
            )}
          </div>
        ))}
      </div>

      <p className="mt-6 text-sm text-gray-400">{t('summary.allGood')}</p>
    </div>
  );
}
